/**
 * Task #245 — Spotlight quick-search palette.
 *
 * Renders a centred cmdk dialog with flat priority-ranked results:
 * Tier 1 (local fuzzy) → Tier 2/3 (server trigram + keyword). Tier 4
 * (deep pulse/email content scan) was removed in favour of relying on
 * the LLM-extracted keyword tier. Single render contract — all rows
 * share `QuickSearchResultRow` regardless of the tier that produced them.
 *
 * Modifier capture for Cmd/Ctrl+Enter (open in new tab): cmdk's
 * `CommandItem.onSelect` does not forward the original keyboard event,
 * so we install a `keydown` listener on the dialog's surrounding div
 * that updates a ref BEFORE cmdk's internal handler fires Enter →
 * onSelect. The ref is then read inside `handleSelect`.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'wouter';
import { Search, Loader2, AlertCircle } from 'lucide-react';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
} from '@/components/ui/command';
import { queryClient } from '@/lib/queryClient';
import { useQuickSearch } from '@/hooks/useQuickSearch';
import { QuickSearchResultRow } from './QuickSearchResultRow';
import { useExpansion } from '@/contexts/expansion-context';
import { setFromSearchIntent } from '@/lib/quick-search/focus-intent';
import { setPinIntent } from '@/lib/quick-search/pin-intent';
import type { SearchHit } from '@/lib/quick-search/types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function QuickSearchDialog({ open, onOpenChange }: Props) {
  const [, navigate] = useLocation();
  const { collapseAllExceptYears } = useExpansion();
  const search = useQuickSearch(open);
  const modifierRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Task #276 — after the user picks any project from the palette,
  // invalidate the recent-projects cache so the next cmd+K open shows
  // the just-opened project at the top. The 60s staleTime alone would
  // make the new top entry lag, defeating the empty-state UX.
  const invalidateRecent = () => {
    queryClient.invalidateQueries({ queryKey: ['/api/search/recent'] });
  };

  useEffect(() => {
    if (!open) search.setQuery('');
  }, [open]);

  // Capture modifier state on the keydown that immediately precedes
  // cmdk's Enter → onSelect dispatch. We intentionally listen in the
  // capture phase so we run before cmdk's bubble-phase handler.
  useEffect(() => {
    if (!open) return;
    const node = containerRef.current;
    if (!node) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        modifierRef.current = e.metaKey || e.ctrlKey;
      }
    };
    node.addEventListener('keydown', onKeyDown, true);
    return () => node.removeEventListener('keydown', onKeyDown, true);
  }, [open]);

  const handleSelect = (hit: SearchHit) => {
    const url = `/?projectId=${encodeURIComponent(hit.projectId)}`;
    const withModifier = modifierRef.current;
    modifierRef.current = false;
    if (withModifier) {
      window.open(url, '_blank', 'noopener,noreferrer');
      invalidateRecent();
      onOpenChange(false);
      return;
    }
    setFromSearchIntent();
    collapseAllExceptYears();
    navigate(url);
    window.dispatchEvent(
      new CustomEvent('navigate-to-project', {
        detail: { projectId: hit.projectId },
      }),
    );
    invalidateRecent();
    onOpenChange(false);
  };

  // Pin-and-focus: replace pinned list with this single project, enable
  // the "show only pinned" filter, expand the project row, and close
  // the dialog. The project-tree listens for `pin-and-focus-project`
  // and does the state updates against `useProjectTreeFilters`.
  const handlePin = (hit: SearchHit) => {
    setFromSearchIntent();
    // Durable intent: ProjectTree consumes it on mount, so pinning
    // from a non-dashboard route (e.g. admin pages) still applies
    // after the navigation lands.
    setPinIntent({ projectId: hit.projectId, title: hit.projectName });
    collapseAllExceptYears();
    navigate(`/?projectId=${encodeURIComponent(hit.projectId)}`);
    // Fast-path event: when ProjectTree is already mounted, this
    // applies the pin instantly without waiting for the mount effect.
    window.dispatchEvent(
      new CustomEvent('pin-and-focus-project', {
        detail: { projectId: hit.projectId, title: hit.projectName },
      }),
    );
    invalidateRecent();
    onOpenChange(false);
  };

  // Task #301 — only treat "no results" as a genuine empty state when the
  // local index actually loaded. If the index couldn't load (cold open during
  // a backend hiccup) we must NOT render "No matches found" — that masquerades
  // a transient data failure as "search is broken / only one project". The
  // hints below never block navigation: any results that DID load still render.
  const indexUnavailable = search.indexUnavailable;
  const indexLoading = search.isIndexLoading;

  const showEmpty =
    search.results.length === 0 &&
    search.query.trim().length > 0 &&
    !search.isQuickSearching &&
    !indexUnavailable &&
    !indexLoading;

  const showIndexLoading =
    indexLoading &&
    search.results.length === 0 &&
    search.query.trim().length > 0;

  const showIndexError =
    indexUnavailable &&
    search.results.length === 0 &&
    search.query.trim().length > 0;

  // Task #276 — empty-state grouping. Pinned (if any) renders ABOVE
  // Recent (if any). When both are empty, render the subtle hint.
  // Typed queries continue to render the flat ranked list with no
  // group headers, matching today's behaviour.
  const isEmptyQuery = search.query.trim().length === 0;
  const showDefaults = isEmptyQuery;
  const hasPinned = showDefaults && search.pinnedResults.length > 0;
  const hasRecent = showDefaults && search.recentResults.length > 0;
  const showDefaultHint = showDefaults && !hasPinned && !hasRecent;

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      // cmdk's default `shouldFilter` would re-score and re-order the
      // list, overriding our ranking pipeline. Disable it so our
      // ranking is the single source of truth.
      commandProps={{ shouldFilter: false }}
    >
      <div ref={containerRef}>
        <CommandInput
          placeholder="Search projects, clients, year, keywords…"
          value={search.query}
          onValueChange={search.setQuery}
          data-testid="quick-search-input"
        />
        <CommandList className="max-h-[60vh]">
          {showEmpty && (
            <CommandEmpty data-testid="quick-search-empty">
              <div className="flex flex-col items-center gap-1 py-6 text-sm text-muted-foreground">
                <Search className="h-4 w-4 opacity-60" />
                <span>No matches found</span>
              </div>
            </CommandEmpty>
          )}
          {showIndexLoading && (
            <div
              className="flex flex-col items-center gap-1 py-6 text-sm text-muted-foreground"
              data-testid="quick-search-index-loading"
            >
              <Loader2 className="h-4 w-4 animate-spin opacity-60" />
              <span>Loading search…</span>
            </div>
          )}
          {showIndexError && (
            <div
              className="flex flex-col items-center gap-2 py-6 text-sm text-muted-foreground"
              data-testid="quick-search-index-error"
            >
              <AlertCircle className="h-4 w-4 text-amber-500" />
              <span>Couldn’t load search data</span>
              <button
                type="button"
                onClick={() => search.retryIndex()}
                className="text-xs font-medium text-primary hover:underline"
                data-testid="quick-search-index-retry"
              >
                Retry
              </button>
            </div>
          )}
          {showDefaultHint && (
            <div
              className="py-6 text-center text-sm text-muted-foreground"
              data-testid="quick-search-default-hint"
            >
              Search for a project to get started.
            </div>
          )}
          {hasPinned && (
            <CommandGroup heading="Pinned" data-testid="quick-search-group-pinned">
              {search.pinnedResults.map(hit => (
                <QuickSearchResultRow
                  key={`pinned-${hit.id}`}
                  hit={hit}
                  onSelect={handleSelect}
                  onPin={handlePin}
                />
              ))}
            </CommandGroup>
          )}
          {hasRecent && (
            <CommandGroup heading="Recent" data-testid="quick-search-group-recent">
              {search.recentResults.map(hit => (
                <QuickSearchResultRow
                  key={`recent-${hit.id}`}
                  hit={hit}
                  onSelect={handleSelect}
                  onPin={handlePin}
                />
              ))}
            </CommandGroup>
          )}
          {!isEmptyQuery && search.results.map(hit => (
            <QuickSearchResultRow
              key={hit.id}
              hit={hit}
              onSelect={handleSelect}
              onPin={handlePin}
            />
          ))}
        </CommandList>
      </div>
    </CommandDialog>
  );
}
